/**
 * IXVerify v2 — capture image plumbing (ixv2 WP-3.2; plan §4, R12).
 *
 * R12 is the law of this file: the CAPTURED ORIGINAL is a certified copy —
 * the full sensor frame, saved untouched. No crop, no perspective correction,
 * no enhancement, ever. The only transcode we perform on a viewfinder capture
 * is the unavoidable canvas→JPEG encode that materializes a frame from a
 * getUserMedia stream (drawn at the FULL video resolution, origin 0,0 — a
 * frame grab, not an edit). Files from the native-camera fallback upload
 * byte-identical (the File object goes to the server as-is).
 *
 * The downscaled RENDITION (≤1568px long edge — the model's vision cap) is
 * model input only, never evidence. EXIF orientation is normalized on the
 * rendition (the v1 CameraCapture fix, kept for the native-camera path where
 * iOS writes orientation tags) because the model should see the page upright;
 * the original keeps its bytes and its tags.
 */

/** The model's vision cap — renditions never exceed this on the long edge (§4). */
export const RENDITION_MAX_EDGE = 1568;
/** Rendition JPEG quality (§4: q≈0.85). */
export const RENDITION_QUALITY = 0.85;
/** Frame-grab encode quality for viewfinder originals (kept high — evidence). */
export const ORIGINAL_CAPTURE_QUALITY = 0.92;
/**
 * Quality floor (§4): below 2,000 px on the stream's long edge (~180 DPI over
 * a Letter page) the in-app viewfinder cannot produce a certified-copy-grade
 * document capture — fall back to the native camera for document shots.
 */
export const QUALITY_FLOOR_LONG_EDGE = 2000;

/** Ideal constraints: highest available 4:3 rear stream (§4 — 4:3 wraps a
 *  portrait Letter sheet almost exactly). */
export const DOCUMENT_STREAM_CONSTRAINTS: MediaStreamConstraints = {
  video: {
    facingMode: 'environment',
    width: { ideal: 4032 },
    height: { ideal: 3024 },
  },
  audio: false,
};

export interface StreamCapability {
  width: number;
  height: number;
  longEdge: number;
  /** True when the negotiated stream can produce document-grade captures. */
  meetsQualityFloor: boolean;
}

/**
 * Per-device stream capability, read empirically from the LIVE track (§4/WP
 * brief: "verified empirically here") — constraints are what we asked for;
 * `getSettings()` is what the device actually granted.
 */
export function probeStreamCapability(stream: MediaStream): StreamCapability {
  const track = stream.getVideoTracks()[0];
  const settings: MediaTrackSettings = track?.getSettings ? track.getSettings() : {};
  const width = settings.width || 0;
  const height = settings.height || 0;
  const longEdge = Math.max(width, height);
  return { width, height, longEdge, meetsQualityFloor: longEdge >= QUALITY_FLOOR_LONG_EDGE };
}

export interface CapturedFrame {
  blob: Blob;
  width: number;
  height: number;
}

function canvasToBlob(canvas: HTMLCanvasElement, type: string, quality: number): Promise<Blob> {
  return new Promise((resolve, reject) => {
    canvas.toBlob(
      (blob) => (blob ? resolve(blob) : reject(new Error('canvas toBlob produced no data'))),
      type,
      quality
    );
  });
}

/**
 * Grab the current viewfinder frame at FULL sensor resolution (R12): the
 * canvas is exactly `videoWidth`×`videoHeight` and the draw is (video, 0, 0)
 * — no source rect, no destination scaling, no transform. The on-screen
 * letterboxed preview and the corner guides are aids, never masks; what is
 * saved is the whole frame.
 */
export async function captureFrameFromVideo(video: HTMLVideoElement): Promise<CapturedFrame> {
  const width = video.videoWidth;
  const height = video.videoHeight;
  if (!width || !height) throw new Error('The camera stream is not ready yet');
  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;
  const ctx = canvas.getContext('2d');
  if (!ctx) throw new Error('Canvas 2D context unavailable');
  ctx.drawImage(video, 0, 0);
  const blob = await canvasToBlob(canvas, 'image/jpeg', ORIGINAL_CAPTURE_QUALITY);
  return { blob, width, height };
}

function loadImageFromBlob(blob: Blob): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(blob);
    const img = new Image();
    img.onload = () => {
      URL.revokeObjectURL(url);
      resolve(img);
    };
    img.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error('Image could not be decoded'));
    };
    img.src = url;
  });
}

/**
 * Build the extraction rendition: downscale to ≤ RENDITION_MAX_EDGE on the
 * long edge, JPEG q≈0.85. Drawing through a canvas also normalizes EXIF
 * orientation (browsers auto-apply the tag — the v1 CameraCapture fix),
 * which matters for native-camera files. Renditions are MODEL INPUT — the
 * untouched original is what the record keeps (R12).
 */
export async function makeRendition(source: Blob): Promise<CapturedFrame> {
  const img = await loadImageFromBlob(source);
  const srcW = img.naturalWidth || img.width;
  const srcH = img.naturalHeight || img.height;
  if (!srcW || !srcH) throw new Error('Image has no dimensions');
  const scale = Math.min(1, RENDITION_MAX_EDGE / Math.max(srcW, srcH));
  const width = Math.max(1, Math.round(srcW * scale));
  const height = Math.max(1, Math.round(srcH * scale));
  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;
  const ctx = canvas.getContext('2d');
  if (!ctx) throw new Error('Canvas 2D context unavailable');
  ctx.drawImage(img, 0, 0, width, height);
  const blob = await canvasToBlob(canvas, 'image/jpeg', RENDITION_QUALITY);
  return { blob, width, height };
}

/** Stop every track on a stream (viewfinder teardown). */
export function stopStream(stream: MediaStream | null | undefined): void {
  if (!stream) return;
  for (const track of stream.getTracks()) {
    try {
      track.stop();
    } catch {
      /* already stopped */
    }
  }
}

/** getUserMedia availability probe (jsdom/older browsers/file:// have none). */
export function cameraSupported(): boolean {
  return typeof navigator !== 'undefined' && !!navigator.mediaDevices?.getUserMedia;
}
